import { create } from 'zustand';
import {
  getUserDirectory,
  getUserStats,
  getDeactivationLogs,
  getUserById,
  createUser,
  updateUser,
  deactivateUser,
  reactivateUser,
  bulkImportUsers,
  getDepartments,
  User,
  UserDetail,
  UserStats,
  DeactivationLog,
  Pagination,
  CreateUserDTO,
  UpdateUserDTO,
  BulkImportUser,
} from '@/services/InstructorService/InstructorUsersService';

interface InstructorUsersState {
  // Data states
  users: User[];
  stats: UserStats | null;
  deactivationLogs: DeactivationLog[];
  departments: string[];
  selectedUser: UserDetail | null;

  // Pagination states
  usersPagination: Pagination | null;
  logsPagination: Pagination | null;

  // Filter states
  searchTerm: string;
  selectedDepartment: string;
  selectedStatus: string;
  currentPage: number;
  itemsPerPage: number;

  // Loading states
  loadingUsers: boolean;
  loadingStats: boolean;
  loadingLogs: boolean;
  loadingUser: boolean;
  creating: boolean;
  updating: boolean;
  deactivating: boolean;
  reactivating: boolean;
  importing: boolean;

  // Error states
  error: string | null;

  // Actions
  fetchUsers: (params?: {
    search?: string;
    status?: string;
    department?: string;
    page?: number;
    limit?: number;
  }) => Promise<void>;
  fetchStats: () => Promise<void>;
  fetchDeactivationLogs: (page?: number, limit?: number) => Promise<void>;
  fetchUserById: (userId: string) => Promise<UserDetail | null>;
  fetchDepartments: () => Promise<void>;
  createUser: (userData: CreateUserDTO) => Promise<{ success: boolean; message: string; data?: { userId: string; generatedPassword: string } }>;
  updateUser: (userId: string, userData: UpdateUserDTO) => Promise<{ success: boolean; message: string }>;
  deactivateUser: (userId: string, reason: string) => Promise<{ success: boolean; message: string }>;
  reactivateUser: (userId: string) => Promise<{ success: boolean; message: string }>;
  bulkImportUsers: (users: BulkImportUser[]) => Promise<{ success: boolean; message: string; data?: { success: number; failed: number } }>;

  // Filter actions
  setSearchTerm: (term: string) => void;
  setSelectedDepartment: (department: string) => void;
  setSelectedStatus: (status: string) => void;
  setCurrentPage: (page: number) => void;
  setItemsPerPage: (limit: number) => void;
  setSelectedUser: (user: UserDetail | null) => void;
  clearFilters: () => void;
}

const useInstructorUsersStore = create<InstructorUsersState>((set, get) => ({
  // Initial states
  users: [],
  stats: null,
  deactivationLogs: [],
  departments: [],
  selectedUser: null,

  usersPagination: null,
  logsPagination: null,

  searchTerm: '',
  selectedDepartment: 'All Department',
  selectedStatus: 'All Status',
  currentPage: 1,
  itemsPerPage: 10,

  loadingUsers: false,
  loadingStats: false,
  loadingLogs: false,
  loadingUser: false,
  creating: false,
  updating: false,
  deactivating: false,
  reactivating: false,
  importing: false,

  error: null,

  // Fetch students with filters and pagination
  fetchUsers: async (params) => {
    const state = get();
    const queryParams = {
      search: params?.search ?? state.searchTerm,
      status: params?.status ?? state.selectedStatus,
      department: params?.department ?? state.selectedDepartment,
      page: params?.page ?? state.currentPage,
      limit: params?.limit ?? state.itemsPerPage,
    };

    set({ loadingUsers: true, error: null });

    try {
      const response = await getUserDirectory(queryParams);

      if (!response.success) {
        throw new Error(response.error?.message ?? 'Failed to fetch students');
      }

      set({
        users: response.data || [],
        usersPagination: response.pagination || null,
        loadingUsers: false,
      });
    } catch (error: any) {
      console.error('InstructorUsersStore: Error in fetchUsers:', error);
      set({
        error: error.message ?? 'Something went wrong',
        loadingUsers: false,
      });
    }
  },

  // Fetch user statistics
  fetchStats: async () => {
    set({ loadingStats: true });

    try {
      const response = await getUserStats();

      if (!response.success) {
        throw new Error(response.error?.message ?? 'Failed to fetch stats');
      }

      set({
        stats: response.data || null,
        loadingStats: false,
      });
    } catch (error: any) {
      console.error('InstructorUsersStore: Error in fetchStats:', error);
      set({ loadingStats: false });
    }
  },

  // Fetch deactivation logs
  fetchDeactivationLogs: async (page = 1, limit = 10) => {
    set({ loadingLogs: true });

    try {
      const response = await getDeactivationLogs(page, limit);

      if (!response.success) {
        throw new Error(response.error?.message ?? 'Failed to fetch logs');
      }

      set({
        deactivationLogs: response.data || [],
        logsPagination: response.pagination || null,
        loadingLogs: false,
      });
    } catch (error: any) {
      console.error('InstructorUsersStore: Error in fetchDeactivationLogs:', error);
      set({ loadingLogs: false });
    }
  },

  // Fetch single student by ID
  fetchUserById: async (userId) => {
    set({ loadingUser: true });

    try {
      const response = await getUserById(userId);

      if (!response.success || !response.data) {
        throw new Error(response.error?.message ?? 'Student not found');
      }

      set({
        selectedUser: response.data,
        loadingUser: false,
      });

      return response.data;
    } catch (error: any) {
      console.error('InstructorUsersStore: Error in fetchUserById:', error);
      set({ loadingUser: false });
      return null;
    }
  },

  // Fetch departments for filter
  fetchDepartments: async () => {
    try {
      const response = await getDepartments();

      if (response.success && response.data) {
        set({ departments: response.data });
      }
    } catch (error: any) {
      console.error('InstructorUsersStore: Error in fetchDepartments:', error);
    }
  },

  // Create new student
  createUser: async (userData) => {
    set({ creating: true });

    try {
      const response = await createUser(userData);

      set({ creating: false });

      if (!response.success) {
        return {
          success: false,
          message: response.error?.message ?? 'Failed to create student',
        };
      }

      // Refresh users list
      get().fetchUsers();
      get().fetchStats();

      return {
        success: true,
        message: 'Student created successfully',
        data: response.data,
      };
    } catch (error: any) {
      console.error('InstructorUsersStore: Error in createUser:', error);
      set({ creating: false });

      return {
        success: false,
        message: error.message ?? 'Failed to create student',
      };
    }
  },

  // Update student
  updateUser: async (userId, userData) => {
    set({ updating: true });

    try {
      const response = await updateUser(userId, userData);

      set({ updating: false });

      if (!response.success) {
        return {
          success: false,
          message: response.error?.message ?? 'Failed to update student',
        };
      }

      // Refresh users list
      get().fetchUsers();

      return {
        success: true,
        message: response.message ?? 'Student updated successfully',
      };
    } catch (error: any) {
      console.error('InstructorUsersStore: Error in updateUser:', error);
      set({ updating: false });

      return {
        success: false,
        message: error.message ?? 'Failed to update student',
      };
    }
  },

  // Deactivate student
  deactivateUser: async (userId, reason) => {
    set({ deactivating: true });

    try {
      const response = await deactivateUser(userId, reason);

      set({ deactivating: false });

      if (!response.success) {
        return {
          success: false,
          message: response.error?.message ?? 'Failed to deactivate student',
        };
      }

      // Refresh data
      get().fetchUsers();
      get().fetchStats();
      get().fetchDeactivationLogs();

      return {
        success: true,
        message: response.message ?? 'Student deactivated successfully',
      };
    } catch (error: any) {
      console.error('InstructorUsersStore: Error in deactivateUser:', error);
      set({ deactivating: false });

      return {
        success: false,
        message: error.message ?? 'Failed to deactivate student',
      };
    }
  },

  // Reactivate student
  reactivateUser: async (userId) => {
    set({ reactivating: true });

    try {
      const response = await reactivateUser(userId);

      set({ reactivating: false });

      if (!response.success) {
        return {
          success: false,
          message: response.error?.message ?? 'Failed to reactivate student',
        };
      }

      // Refresh data
      get().fetchUsers();
      get().fetchStats();
      get().fetchDeactivationLogs();

      return {
        success: true,
        message: response.message ?? 'Student reactivated successfully',
      };
    } catch (error: any) {
      console.error('InstructorUsersStore: Error in reactivateUser:', error);
      set({ reactivating: false });

      return {
        success: false,
        message: error.message ?? 'Failed to reactivate student',
      };
    }
  },

  // Bulk import students
  bulkImportUsers: async (users) => {
    set({ importing: true });

    try {
      const response = await bulkImportUsers(users);

      set({ importing: false });

      if (!response.success) {
        return {
          success: false,
          message: response.error?.message ?? 'Failed to import students',
        };
      }

      // Refresh data
      get().fetchUsers();
      get().fetchStats();

      return {
        success: true,
        message: response.message ?? 'Students imported successfully',
        data: response.data,
      };
    } catch (error: any) {
      console.error('InstructorUsersStore: Error in bulkImportUsers:', error);
      set({ importing: false });

      return {
        success: false,
        message: error.message ?? 'Failed to import students',
      };
    }
  },

  // Filter actions
  setSearchTerm: (term) => set({ searchTerm: term }),
  setSelectedDepartment: (department) => set({ selectedDepartment: department }),
  setSelectedStatus: (status) => set({ selectedStatus: status }),
  setCurrentPage: (page) => set({ currentPage: page }),
  setItemsPerPage: (limit) => set({ itemsPerPage: limit }),
  setSelectedUser: (user) => set({ selectedUser: user }),

  clearFilters: () =>
    set({
      searchTerm: '',
      selectedDepartment: 'All Department',
      selectedStatus: 'All Status',
      currentPage: 1,
    }),
}));

export default useInstructorUsersStore;
